import React, { forwardRef, MutableRefObject, useEffect, useImperativeHandle, useMemo } from 'react'
import './styles/notification-builder.scss'

import { getClassNames } from '../../_utils/classnames'
import { useMountedRef } from '../../_utils/use-mounted-ref'
import { PromiseableProp } from '../../_utils/promiseable-prop'
import { DomainDto } from '../../dtos/domain'
import { GetNotificationResponse } from '@pushly/models/lib/structs/notifications'
import {
    INotificationBuilderState,
    NotificationBuilderMacroProps,
    TemplateDetails,
} from './interfaces/notification-builder-state.interface'
import { NotificationTestConfigurationModel } from '../../models/notification/notification-test-configuration.model'
import { NotificationAudienceModel } from '../../models/notification/notification-audience.model'
import {
    getVariantTtlConfig,
    resolveNotificationsProp,
    resolveTemplatesProp,
    normalizeNotificationDeliveries,
    validateSubmit,
} from './helpers'
import { useRefEffect } from '../../hooks/use-ref-effect'
import { NotificationVariantModel } from '../../models/notification/notification-variant.model'
import { NotificationDeliveryModel } from '../../models/notification/notification-delivery.model'
import { NotificationBuilderLevel, NotificationBuilderTheme } from './enums'
import { useService } from '../../hooks/use-service'
import { AccountDto } from '../../dtos/account.dto'
import { NotificationDeliveryWindow } from '../../enums/notification-delivery-window'
import { NotificationDeliveryType } from '../../enums/notification-delivery-type'
import * as moment from 'moment-timezone'
import FlagList from '../../structs/flags-list'
import { AccountService, AppService } from '../../services'
import { getEnabledDeliveryChannels } from '../../_utils/domain'
import { getAccountEnabledDeliveryChannels } from '../../_utils/account'
import { ActionDispatcher } from './types'
import { getBuilderStateReducer, useActionDispatcher } from './reducers'
import { CreateDomainNotificationRequest } from '@pushly/models/lib/structs/notifications/create-requests/create-domain-notification-request'
import { GetNamedNotificationTemplateResponse } from '@pushly/models/lib/structs/notifications'
import { Journey } from '../../features/journeys/types/journey-api-response'
import { CatalogItemType, DeliveryChannel, NotificationAbTestType } from '@pushly/models/lib/enums'
import { CombinedItemStrategy } from '@pushly/models/lib/enums/catalogs'
import { UserPreferencesApi, useUserPreferences } from '@pushly/aqe/lib/providers/user-preferences-provider/core/index'
import { AppState } from '../../stores/app'
import { Flex } from 'antd'
import { getDefaultCollapseKeyForDomainType } from '@pushly/aqe/lib/utils/notification-collapse-key'
import { usePushlyAi } from './hooks/use-pushly-ai'
import { usePageAnalyzer } from './hooks/use-page-analyzer'
import { notification } from 'antd'
import { antdStaticApi } from '../../providers/antd-app-provider/antd-static-api'

type NotificationBuilderValidator = (
    builder: INotificationBuilderState,
    domain: DomainDto,
    flags: FlagList,
    isPreview?: boolean,
    isDraft?: boolean,
    appService?: AppService,
) => Promise<boolean>

export interface INotificationBuilderCustomRenderProps {
    builder: INotificationBuilderState
    dispatchChanges: ActionDispatcher
    onChannelChange: (change: DeliveryChannel[]) => void
    onDetailsChange: (change: TemplateDetails) => void
    onTestChange: (test: NotificationTestConfigurationModel) => void
    onAudienceChange: (audience: NotificationAudienceModel) => void
    onDistributionChange: (builder: INotificationBuilderState) => void
    variants: NotificationVariantModel[]
    onVariantChange: (variant: NotificationVariantModel, id: number) => void
    onSelectedTemplateChange: (change: { id?: number; domain: DomainDto }) => void
    onInitialVariantInit?: (variant: NotificationVariantModel) => void
    validateSubmit: NotificationBuilderValidator
}

export type NotificationBuilderRef = {
    state: INotificationBuilderState
    validate: NotificationBuilderValidator
    dispatch: ActionDispatcher
}

export type NotificationBuilderProps = {
    ref?: MutableRefObject<NotificationBuilderRef>
    className?: string
    domain: DomainDto
    account?: AccountDto
    journey?: Journey
    theme?: NotificationBuilderTheme
    level?: NotificationBuilderLevel
    mode?: 'create' | 'edit'
    type?: 'template' | 'notification'
    notifications?: PromiseableProp<GetNotificationResponse[] | undefined>
    templates?: PromiseableProp<GetNamedNotificationTemplateResponse[] | CreateDomainNotificationRequest[] | undefined>
    macroProps?: NotificationBuilderMacroProps
    onInitialVariantInit?: (variant: NotificationVariantModel) => void
    defaultSelectedNotifId?: number
    defaultSelectedTemplateId?: number
    defaultChannels?: DeliveryChannel[]
    showWebhookOptions?: boolean
    selectedCatalogItemPickStrategy?: CombinedItemStrategy
    catalogItemType?: CatalogItemType
    catalogItemPickStrategyOptions?: CombinedItemStrategy[]
    onChange?: (builder: INotificationBuilderState) => any
    children: (props: INotificationBuilderCustomRenderProps) => JSX.Element
}

// Runs once on reducer init
const getBuilderStateInitializer =
    (
        domain: DomainDto,
        userPreferences: UserPreferencesApi,
        onInitialVariantInit?: (variant: NotificationVariantModel) => void,
        account?: AccountDto,
        journey?: Journey,
    ) =>
    (initialState: INotificationBuilderState) => {
        return {
            ...initialState,
            variants: [
                getInitialVariant(domain, account, initialState, userPreferences, onInitialVariantInit, journey),
            ],
        }
    }

function getInitialDeliveryModel(level: NotificationBuilderLevel, domain: DomainDto): NotificationDeliveryModel {
    const isDomainLevel = level === NotificationBuilderLevel.DOMAIN
    const delivery = NotificationDeliveryModel.build({})

    if (isDomainLevel) {
        const defaultType = domain.displayMeta.default_delivery_type
        const defaultWindow = domain.displayMeta.default_delivery_window

        delivery.setType(defaultType ?? NotificationDeliveryType.IMMEDIATE)
        delivery.setWindow(defaultWindow ?? NotificationDeliveryWindow.STANDARD)

        // ensure a default date/time and time zone are set for
        //  domains with a non-imm/std default combination
        if (defaultType === NotificationDeliveryType.SCHEDULED) {
            const now = moment()
            if (defaultWindow === NotificationDeliveryWindow.INFORMED) {
                now.hour(0).minute(0)
            }

            delivery.setSendDate(now.toISOString())

            if (defaultWindow !== NotificationDeliveryWindow.TIMEZONE) {
                delivery.setTimeZone(domain.timezone)
            }
        }
    }

    return delivery
}

export const getInitialVariant = (
    domain: DomainDto,
    account: AccountDto | undefined,
    state: INotificationBuilderState,
    userPreferences: UserPreferencesApi,
    onInitialVariantInit?: (variant: NotificationVariantModel) => void,
    journey?: Journey | undefined,
) => {
    const isOrgLevel = state.level === NotificationBuilderLevel.ORG
    const isDomainLevel = state.level === NotificationBuilderLevel.DOMAIN
    const isJourneyLevel = state.level === NotificationBuilderLevel.CAMPAIGN

    const variant = NotificationVariantModel.build({})

    const content = variant.getContent().getDefaultContent()
    const iconUrl = isOrgLevel
        ? null
        : isJourneyLevel && journey?.configuration?.audience?.default_icon_url
        ? journey.configuration.audience.default_icon_url
        : domain.defaultIconUrl
    content.setIconUrl(iconUrl)
    content.setBadgeUrl(isOrgLevel ? null : domain.defaultBadgeUrl)
    content.setIsUsingDomainDefaultIcon(isOrgLevel)
    content.setIsUsingDomainDefaultBadge(isOrgLevel)
    if (isOrgLevel && !!account?.defaultNotificationLandingUrl) {
        content.setLandingUrl(account.defaultNotificationLandingUrl)
    }

    // Use default values if provided
    onInitialVariantInit?.(variant)

    const expOpts = content.getExperienceOptions()
    expOpts.setCollapseKey(getDefaultCollapseKeyForDomainType(domain.collapseKeyType))
    // pass null (ogr context) to use global defaults
    const ttlSet = getVariantTtlConfig(isOrgLevel ? account : domain, expOpts)
    expOpts.setTtlSeconds(ttlSet.seconds)
    expOpts.setTtlMetric(ttlSet.metric)

    const delivery = getInitialDeliveryModel(state.level, domain)
    if (isDomainLevel) {
        const defaultType = userPreferences.get(['notification_delivery_type'])
        const defaultWindow = userPreferences.get(['notification_delivery_strategy'])

        delivery.setType(defaultType ?? NotificationDeliveryType.IMMEDIATE)
        delivery.setWindow(defaultWindow ?? NotificationDeliveryWindow.STANDARD)

        // ensure a default date/time and time zone are set for
        //  domains with a non-imm/std default combination
        if (defaultType === NotificationDeliveryType.SCHEDULED) {
            const now = moment()
            if (defaultWindow === NotificationDeliveryWindow.INFORMED) {
                now.hour(0).minute(0)
            }

            delivery.setSendDate(now.toISOString())

            if (defaultWindow !== NotificationDeliveryWindow.TIMEZONE) {
                delivery.setTimeZone(domain.timezone)
            }
        }
    }

    variant.setDelivery(delivery)

    return variant
}

const NotificationBuilder = forwardRef<NotificationBuilderRef, NotificationBuilderProps>((props, ref) => {
    const {
        className,
        domain,
        account,
        journey,
        notifications,
        onChange,
        showWebhookOptions,
        selectedCatalogItemPickStrategy,
        catalogItemType,
        catalogItemPickStrategyOptions,
        onInitialVariantInit,
        macroProps,
    } = props

    const appState = useService(AppState)
    const accountSvc = useService(AccountService)
    const [_mounted, runIfMounted] = useMountedRef()

    const mode = props.mode === 'edit' ? 'edit' : 'create'
    const type = props.type === 'template' ? 'template' : 'notification'

    const theme = props.theme ?? NotificationBuilderTheme.STANDARD
    const themeClassName = theme === NotificationBuilderTheme.MOBILE ? 'mobile' : 'standard'

    const level = props.level ?? NotificationBuilderLevel.DOMAIN
    const isOrgLevel = level === NotificationBuilderLevel.ORG
    const isDomainLevel = level === NotificationBuilderLevel.DOMAIN
    const isJourneyLevel = level === NotificationBuilderLevel.CAMPAIGN
    const isJourneyOrDomainLevel = isJourneyLevel || isDomainLevel
    const levelClassName = isJourneyLevel ? 'journey' : isOrgLevel ? 'org' : 'domain'

    const userPreferences = useUserPreferences(
        appState.currentUser?.id,
        isJourneyOrDomainLevel ? props.domain.id : undefined,
        isOrgLevel ? props.account?.id : undefined,
    )

    let initialChannels = isDomainLevel
        ? getEnabledDeliveryChannels(domain, true)
        : account
        ? getAccountEnabledDeliveryChannels(account!)
        : []

    if (props.defaultChannels) {
        initialChannels = props.defaultChannels
    }

    if (!isJourneyLevel) {
        const userChannelsState = userPreferences.get(['last_targeted_delivery_channels'])

        // TODO: remove !isJourneyLevel once channels are fully implemented in journeys
        if (mode === 'create' && userChannelsState && !isJourneyLevel && type !== 'template') {
            initialChannels = userChannelsState
        }
    }

    const pushlyAi = usePushlyAi(domain)
    const pageAnalyzer = usePageAnalyzer(domain)

    const initialState: INotificationBuilderState = {
        theme,
        level,
        mode,
        type,
        availableDomainsLoaded: false,
        availableDomains: [],
        domain,
        org: account,
        journey,
        loading: true,
        initialNotificationStateLoaded: false,
        initialNotificationState: [],
        availableSegmentsLoaded: false,
        availableSegments: [],
        reachEstimateLoaded: false,
        test: undefined,
        variants: [],
        channels: initialChannels,
        channelDependencies: {
            [DeliveryChannel.NATIVE_ANDROID]: {
                loading: false,
                notificationChannels: [],
            },
        },
        details: { name: '' },
        initialTemplatesStateLoaded: false,
        availableTemplates: [],
        selectedTemplate: undefined,
        usingTemplate: false,
        selectedVariantIdx: 0,
        showWebhookOptions,
        selectedCatalogItemPickStrategy: selectedCatalogItemPickStrategy ?? undefined,
        catalogItemType,
        catalogItemPickStrategyOptions,
        macroProps,
        stzAvailabilityValidating: false,
        stzAvailabilityCache: new Map(),
        pageAnalyzer,
        pushlyAi,
    }

    const [value, _dispatch] = React.useReducer(
        getBuilderStateReducer,
        initialState,
        getBuilderStateInitializer(domain, userPreferences, onInitialVariantInit, account, journey),
    )

    const dispatch = useActionDispatcher(_dispatch, onChange)

    // Track A/B test mode transitions at this level because NotificationVariantManager
    // unmounts/remounts when selectedVariantIdx changes (due to key={activeVariant.getId()}).
    // Placing the detection here ensures the ref survives variant switches.
    const prevVariantCount = React.useRef(value.variants.length)
    useEffect(() => {
        const prev = prevVariantCount.current
        const curr = value.variants.length
        const wasAbTest = prev > 1
        const isAbTest = curr > 1

        if (!wasAbTest && isAbTest) {
            const addedCount = curr - prev
            const isBatchAdd = addedCount > 1

            antdStaticApi.notification.info({
                title: isBatchAdd ? `A/B Testing enabled with ${curr} variants` : 'A/B Testing enabled',
                description: 'Each test variant will be delivered to a portion of your audience.',
            })
        }

        prevVariantCount.current = curr
    }, [value.variants.length])

    // Update root reference when the pushlyAi instance changes
    useEffect(() => {
        dispatch({ type: 'patch', entity: 'root', data: { pushlyAi } })
    }, [pushlyAi])

    // Update root reference when the pageAnalyzer instance changes
    useEffect(() => {
        dispatch({ type: 'patch', entity: 'root', data: { pageAnalyzer } })
    }, [pageAnalyzer])

    useImperativeHandle(
        ref,
        () => ({
            state: value,
            validate: validateSubmit,
            dispatch,
        }),
        [value, validateSubmit, dispatch],
    )

    useEffect(() => {
        const loadAvailableDomains = async () => {
            if (isOrgLevel) {
                const accountDomains = await accountSvc.fetchAccountDomains(account!.id, { pagination: 0 })
                dispatch({ type: 'patch', entity: 'available-domains', data: accountDomains })
            } else {
                dispatch({ type: 'patch', entity: 'available-domains', data: [domain] })
            }
        }

        if (!value.availableDomainsLoaded) {
            loadAvailableDomains()
        }
    }, [level, value.org?.id, domain.id, value.availableDomainsLoaded])

    useRefEffect(() => {
        const resolvePromisableProps = async () => {
            let initialUpdate: Partial<INotificationBuilderState> = {
                loading: false,
            }

            const notifUpdate = await resolveNotificationsProp(
                domain,
                notifications,
                value,
                props.defaultSelectedNotifId,
            )
            initialUpdate = { ...initialUpdate, ...notifUpdate }

            if (mode === 'create' && isDomainLevel) {
                const templateUpdate = await resolveTemplatesProp(domain, value, props.defaultSelectedTemplateId)
                initialUpdate = { ...initialUpdate, ...templateUpdate }

                normalizeNotificationDeliveries(
                    mode,
                    level,
                    domain,
                    initialUpdate.test,
                    initialUpdate.variants,
                    getInitialDeliveryModel,
                )
            }

            return runIfMounted(() => {
                dispatch({ type: 'patch', entity: 'root', data: initialUpdate })
            })
        }

        return resolvePromisableProps()
    }, [mode, level, domain.id])

    const onTestChange = React.useCallback((test: NotificationTestConfigurationModel) => {
        dispatch({ type: 'patch', entity: 'test', data: test })
    }, [])

    const onChannelChange = React.useCallback(async (channels: DeliveryChannel[]) => {
        dispatch({ type: 'patch', entity: 'channel', data: channels })
        await userPreferences.set(['last_targeted_delivery_channels'], channels)
    }, [])

    const onDetailsChange = React.useCallback((change: TemplateDetails) => {
        dispatch({ type: 'patch', entity: 'detail', data: change })
    }, [])

    const onAudienceChange = React.useCallback(
        (audience: NotificationAudienceModel) => {
            dispatch({ type: 'patch', entity: 'audience', data: { audience, domain } })
        },
        [domain.id],
    )

    const onDistributionChange = React.useCallback((builder: INotificationBuilderState) => {
        dispatch({ type: 'patch', entity: 'root', data: builder })
    }, [])

    const onVariantChange = React.useCallback((variant: NotificationVariantModel, id: number) => {
        dispatch({ type: 'patch', entity: 'variant', data: { id, variant } })
    }, [])

    const onSelectedTemplateChange = React.useCallback(
        (change: { id: number; domain: DomainDto }) => {
            dispatch({
                type: 'patch',
                entity: 'selected-template',
                data: change,
                userPreferences,
                getInitialVariant,
                onInitialVariantInit,
            })
        },
        [userPreferences],
    )

    const customRenderProps: INotificationBuilderCustomRenderProps = useMemo(
        () => ({
            builder: value,
            dispatchChanges: dispatch,
            onChannelChange,
            onDetailsChange,
            onTestChange,
            onAudienceChange,
            variants: value.variants,
            onDistributionChange,
            onVariantChange,
            onSelectedTemplateChange,
            onInitialVariantInit,
            validateSubmit,
        }),
        [
            value,
            dispatch,
            onChannelChange,
            onDetailsChange,
            onTestChange,
            onAudienceChange,
            value.variants,
            onDistributionChange,
            onVariantChange,
            onSelectedTemplateChange,
            onInitialVariantInit,
            validateSubmit,
        ],
    )

    return (
        <Flex
            justify="flex-start"
            gap={8}
            style={{ flexDirection: 'row-reverse', width: '100%' }}
            className={getClassNames(
                'notification-builder',
                className,
                `mode-${mode}`,
                `theme-${themeClassName}`,
                `level-${levelClassName}`,
            )}
        >
            {props.children(customRenderProps)}
        </Flex>
    )
})

export default NotificationBuilder
